/**
 * Website Monitoring Types
 * Types for website/URL monitoring via Blackbox Exporter
 */

export type WebsiteStatus = 'up' | 'down' | 'pending' | 'paused' | 'unknown';
export type ProbeType = 'http_2xx' | 'ssl_cert';

export interface Website {
  id: number;
  url: string;
  name: string;
  customer_id: number | null;
  customer_name?: string;
  probe_type: ProbeType;
  check_interval: string;
  timeout: string;
  status: WebsiteStatus;
  last_check: string | null;
  response_time_ms: number | null;
  last_error: string | null;
  enabled: boolean;
  // SSL Certificate info
  ssl_expiry_days: number | null;
  ssl_issuer?: string | null;
  ssl_expiry_date?: string | null;
  // Uptime percentages (calculated from Prometheus)
  uptime_24h: number | null;
  uptime_7d: number | null;
  uptime_30d: number | null;
  // Downtime tracking
  downtime_started?: string | null;
  // Metadata
  created_at: string;
  updated_at?: string;
  tags?: string[];
}

export interface WebsiteSummary {
  total: number;
  up: number;
  down: number;
  paused: number;
  avg_response_time_ms: number | null;
  ssl_expiring_soon: number;
}

export interface WebsiteTestResult {
  success: boolean;
  reachable: boolean;
  status_code?: number;
  response_time_ms?: number;
  content_length?: number;
  error?: string;
  ssl?: {
    valid: boolean;
    issuer: string;
    expiry: string;
    expiry_days: number;
  };
  dns?: {
    resolved_ip: string;
    resolution_time_ms: number;
  };
  redirect_chain?: string[];
}

export interface WebsiteHistoryIncident {
  started_at: string;
  ended_at: string | null;
  duration_seconds: number;
  error: string;
}

export interface WebsiteHistoryDataPoint {
  timestamp: string;
  value: number;
}

export interface WebsiteHistory {
  website_id: number;
  period: '1h' | '24h' | '7d' | '30d';
  uptime_percent: number;
  avg_response_time_ms: number;
  min_response_time_ms: number;
  max_response_time_ms: number;
  total_checks: number;
  failed_checks: number;
  incidents: WebsiteHistoryIncident[];
  response_times: WebsiteHistoryDataPoint[];
}

export interface CreateWebsiteRequest {
  url: string;
  name?: string;
  customer_id?: number;
  probe_type?: ProbeType;
  check_interval?: string;
  timeout?: string;
  expected_status_codes?: number[];
  follow_redirects?: boolean;
  verify_ssl?: boolean;
  alert_after_failures?: number;
  tags?: string[];
  port?: number;
}

export interface UpdateWebsiteRequest {
  name?: string;
  customer_id?: number;
  probe_type?: ProbeType;
  check_interval?: string;
  timeout?: string;
  enabled?: boolean;
  tags?: string[];
}

export interface WebsiteFilters {
  status?: WebsiteStatus | 'all';
  customer_id?: number | null;
  search?: string;
}

// Common SSL ports for SSL-only monitoring
export const SSL_PORTS = [
  { port: 443, label: 'HTTPS (443)', description: 'Standard HTTPS port' },
  { port: 465, label: 'SMTPS (465)', description: 'SMTP over SSL' },
  { port: 587, label: 'SMTP/TLS (587)', description: 'SMTP with STARTTLS' },
  { port: 993, label: 'IMAPS (993)', description: 'IMAP over SSL' },
  { port: 995, label: 'POP3S (995)', description: 'POP3 over SSL' },
  { port: 636, label: 'LDAPS (636)', description: 'LDAP over SSL' },
  { port: 8443, label: 'HTTPS Alt (8443)', description: 'Alternative HTTPS port' },
];
